import VuexStore from '@/store'



import {
	LAST_FIRST_REGEX,
	FIRST_LAST_REGEX,
	EMAIL_REGEX,
	PHONE_REGEX,
	LABELED_PHONE_REGEX,
    CODE_REGEX
} from './regex'

const IGNORE_EMAIL = [
    'noemail@noemail.com',
    'doesnotuseemail@gmail.com',
    'noemail@sutter.com',
    'noemail@yahoo.com',
    'noemail@epic-care.com',
    'noemail@noemail.com',
    'no@noemail.com',
    'noemail@gmail.com'
]

const IGNORE_EMAIL_REGEX = /^(((\w|\d)*\+(\w|\d)*)?(no|noemail|doesnotuseemail)@(\w|\s|\d|.)*|(((\w|\d)*\+(\w|\d)*)?(\w|\s|\d)*@(noemail|email|example).(com|org|net)))/i

import {
    COLUMN,
    NAME_COLUMN,
    PHONE_COLUMN,
    EMAIL_COLUMN
} from './column'

// Utility functions
import sameDay from '@/module/util/same-day'
import {
    eventKeywordMap,
    eventProfileMap
} from './event-map'
import {
    patientPhoneMap,
    patientEmailMap
} from './patient-map'

const ONE_DAY = 86400000

/**
 * An event object for scheduling or rescheduling
 * @typedef {Object} EventObj
 * @property {String} id The id of the event
 * @property {[String]} match The array of matches for events
 * @property {String} provider The current provider for the event
 * @property {Number} row The row in order of excel (`patient.row`) which this event represents
 * @property {Number} time Timestamp for the event
 */

/**
 * An object containing basic info about an event
 * @typedef {Object} EventInfo
 * @property {String} id The id of the event in the system
 * @property {String} event The event id of the event
 * @property {String} time The time for the event
 */

/**
 * Checks if the email is a valid email to consider
 *
 * @param {String} email - Email to be checked
 * @returns
 */
function isValidEmail(email) {
    const sanitisedEmail = email.toLowerCase().trim()
    return sanitisedEmail.match(EMAIL_REGEX) &&
        !IGNORE_EMAIL.includes(sanitisedEmail) &&
        !IGNORE_EMAIL_REGEX.test(sanitisedEmail)
}

function toSearchArray(str) {
    return str.toLowerCase().replace(/[^a-z0-9]/, ' ').trim().split(/\s+/).filter((s) => s.length > 0)
}

/**
 * Converts string to lower case, removes `dr.` if present
 * and split into an array by space
 *
 * @param {String} name - Name to be split
 * @returns {[String]}
 */
function splitifyName(name) {
    if(typeof name === 'string' || name instanceof String)
        return name.toLowerCase().replace('dr.', '').trim().split(' ')
    else
        throw 'Not a String Value'
}

/**
 * Checks if `name` matches the `contentName`
 * inputed from user
 *
 * @param {[String]} nameArray - The splitted name of doctor to be matched
 * @param {String} contentName - The input content from user to be matched
 *
 * @returns {Boolean} `true` if matches, `false` if doesn't
 */
function isNameMatch(nameArray, contentName) {
    if(!contentName)
        return false
    return nameArray.every((name) => contentName.toLowerCase().includes(name))
}

/**
 *  Returns the first match position for the `nameArray`
 * in `contentName`
 *
 * @param {[String]} nameArray
 * @param {String} contentName
 * @returns {Number}
 */
function matchPosition(nameArray, contentName) {
    if(!isNameMatch(nameArray, contentName))
        return -1
    return nameArray.reduce((earliestPosition, currentName) => {
        return contentName.toLowerCase().indexOf(currentName) < earliestPosition ? contentName.toLowerCase().indexOf(currentName) : earliestPosition
    }, contentName.length)
}


/**
 * Return the matching provider with event keywords for a `doctorName`.
 *
 * @param {String} doctorName
 * @param {[*]} eventProviderKeyword
 *
 * @returns {*|Boolean}
 */
function matchProvider(doctorName, eventProviderKeyword) {
    // Return the first and only element of `eventProviderKeyword` if its length is 1
    // It corresponds to a case where you have only one provider for the account
    if(eventProviderKeyword.length === 1)
        return eventProviderKeyword[0]

    // Find all matching providers for the doctorName
    const matchingProviders = eventProviderKeyword
        // Map `matchPosition` to all the providers
        .map((provider) => ({...provider, matchPosition: matchPosition(splitifyName(provider.providerName), doctorName)}))
        // Filter out in case of `macthPosition` is -1 i.e. not a match
        .filter((provider) => provider.matchPosition !== -1)

    // Set provider if it matches to only one
    if(matchingProviders.length === 1)
        return matchingProviders[0]
    // Return the provider with least match position
    return matchingProviders.reduce((firstMatch, currProvider) =>   {
        return firstMatch.matchPosition < currProvider.matchPosition ? firstMatch : currProvider
    }, matchingProviders[0])
}

/**
 * Returns a map of all scheduled events system id's to
 * provider for the event
 *
 * @returns {Object<String, String>}
 */
function getScheduleEventMap() {
    try {
        return VuexStore.state.provider.scheduledEventMap
    } catch (error) {
        console.error('Problem setting scheduleEventMap ', error)
        return {}
    }
}

/**
 * Returns future events for a patient flattened out in array containing
 * the event id, the system id and the time filtered by Current provider
 *
 * @param {Object<String, Object>} scheduleEvents Object with event id as keys for event schedule for the event
 * @param {Object<String, String>} scheduledEventMap Key pair values of event message id with provider for message
 * @param {String} currentProvider The current provider for filtering events
 * @returns {[EventInfo]}
 */
function getFutureEvents(scheduleEvents, scheduledEventMap, currentProvider) {
    try {
        if(!currentProvider || !scheduleEvents)
            return []
        return Object.entries(scheduleEvents).flatMap(([event, scheduleObj]) => {
            return Object.entries(scheduleObj)
                .filter(([time, id]) => time > Date.now() - ONE_DAY && (currentProvider && scheduledEventMap[id] == currentProvider))
                .map(([time, id]) => ({
                    event,
                    id,
                    time
                }))
        })
    } catch (error) {
        console.error('Problem in getting future events - ', error)
        return []
    }
}


/**
 * Parses events for patients list
 *
 * @param {[*]} patients - List of patients
 * @param {[*]} eventProviderKeyword - Array of eventProviderKeyword
 * @returns [*]
 */
export function parseEvents(patients, eventProviderKeyword, columnMatchEvent) {
    // Going through each patient and mapping results
    return patients.map((patient) => {
        // Check if patient was marked with invalid provider & provider has not been set
        if(patient.invalidProvider && columnMatchEvent && patient.data.event.some((event) => event.provider)) {

            // Defines `patient.eventMatch` to be an array of empty objects of same length as `patient.row`
            patient.eventMatch = patient.row.map((row) => ({}))

            // Going through each row data i.e. each event data
            patient.row.forEach((rowArr, rowIndex) => {
                /**
                 * The index in `patient.data.event` that matches current `rowIndex`
                 * @type {Number}
                 */
                const eventIndex = patient.data.event.findIndex((event) => event.row === rowIndex)
                // Return if no event found for this row
                if(eventIndex === -1)
                    return
                /**
                 * The event object from `patient.data.event` for the current `rowIndex`
                 * @type {EventObj}
                 */
                const eventObj = patient.data.event[eventIndex]


                // The provider id of the current event obj
                const providerId = eventObj.provider
                // Return if provider not set
                if(!providerId)
                    return
                // Find the provider of the patient from `eventProviderKeyword`
                const provider = eventProviderKeyword.find((provider) => provider.providerId === providerId)

                // Defines `eventKeyword` object which is a map of all keywords to there prospective events
                const eventKeyword = eventKeywordMap(provider.events)

                // Defines `eventKeywords` array which is a list of all keys of all the events
                const eventKeywords = Object.keys(eventKeyword)

                // Checking for all columns matches in that row
                Object.keys(columnMatchEvent).forEach((eventColumn) => {

                    // Gets the term to be searched in `rowArr`
                    const search = rowArr[eventColumn] ? rowArr[eventColumn].toLowerCase().trim() : ''

                    // Cheacks if keyword present as assigned different weightage accordingly
                    if (eventKeywords.includes(search))
                        Object.keys(eventKeyword[search]).forEach((eventId) => {
                            patient.eventMatch[rowIndex][eventId] = (patient.eventMatch[rowIndex][eventId] || 0) + 10
                        })
                    else
                        eventKeywords.forEach((keyword) => {
                            let matchRegex = new RegExp("\\b" + keyword + "\\b")
                            if (search.match(matchRegex))
                                Object.keys(eventKeyword[keyword]).forEach((eventId) => {
                                    patient.eventMatch[rowIndex][eventId] = (patient.eventMatch[rowIndex][eventId] || 0) + 2
                                })
                        })
                })
                // All eventMatches for `rowIndex` complete , we can start finding highest matches
                // If no matches found we can return
                if(Object.keys(patient.eventMatch[rowIndex]).length === 0)
                    return
                // Calculating highest match score and events that matched it
                const highestMatches = Object.entries(patient.eventMatch[rowIndex]).reduce((matchAcc, [eventId, value]) => {
                    if(matchAcc.value < value)
                        return {
                            value,
                            match: [eventId]
                        }
                    else if(matchAcc.value === value) {
                        matchAcc.match.push(eventId)
                        return matchAcc
                    }
                    else
                        return matchAcc
                }, {value: 0, match: []})

                /**
                 * matchId set if highest match is just for single event
                 * @type {String}
                 */
                const matchId = highestMatches.match.length == 1 ? highestMatches.match[0] : null

                /**
                 * Whether the event is an existing event
                 * @type {Boolean}
                 */
                let isExistingEvent = false
                /**
                 * Event in the future that has to be
                 * rescheduled from current row data
                 * @type {Boolean|EventInfo}
                 */
                let rescheduleEvent = false

                // If the patient already exists
                if(patient.existing) {
                    /**
                     * Get future events for the patient with the current provider
                     */
                    const futureEvents = getFutureEvents(patient.data.schedule_event, getScheduleEventMap(), providerId)

                    // Get list of existing events filtered by `matchId`
                    const existingTimes = futureEvents ? futureEvents.filter((eventObj) => eventObj.event === matchId) : []

                    // If you have single match and existing events in the future that match them
                    if (matchId && existingTimes.length > 0) {
                        // Is marked an existing event if any of the existing event is on the same day
                        isExistingEvent = existingTimes.some((event) => sameDay(event.time, eventObj.time))
                        // The first match chosen for reschedule as default
                        rescheduleEvent = existingTimes[0]
                    }

                }

                if(isExistingEvent) {
                    patient.sameSchedule = true
                    if(!patient.scheduleData)
                        patient.scheduleData = []
                    patient.data.event.splice(eventIndex, 1)
                    patient.scheduleData.push({
                        ...eventObj,
                        id: matchId
                    })
                }
                else if(rescheduleEvent) {
                    patient.reschedule = true
                    if(! patient.rescheduleData)
                        patient.rescheduleData = []
                    patient.data.event.splice(eventIndex, 1)
                    patient.rescheduleData.push({
                        ...eventObj,
                        id: rescheduleEvent.id,
                        matchId: rescheduleEvent.event,
                        previous_time: parseInt(rescheduleEvent.time)
                    })
                }
                // If a single match, set event for that match
                else if(matchId) {
                    patient.data.event[eventIndex].id = matchId
                }

                // If multiple matches than   set Id as null
                else if(highestMatches.match.length > 1)
                    patient.data.event[eventIndex].id = null

                // Set match for event
                patient.data.event[eventIndex].match = highestMatches.match
            })

            patient.confirmed = patient.data.event.every((event) => event.id) ? null : false
            patient.invalidProvider = false
        }
        return patient
    })
}

function inferColumns(rows, eventKeyword, doctorNameList) {
    // Stores mapping of column key -> { column index -> count }
    var columnMatch = {}
    COLUMN.forEach((c) => { columnMatch[c.key] = {} })
    rows.forEach((row) => {
        row.forEach((content, index) => {
            if(!isNaN(content))
                content = content.toString()
            content = content.trim()

            // Iterate over each predefined column in order of priority
            for (let c = 0 ; c < COLUMN.length ; c++) {
                let column = COLUMN[c]
                // If this is a column to extract event information
                if (column.event) {
                    let search = content.toLowerCase()
                    if (eventKeyword.some((eventKeyword) => search.includes(eventKeyword)))
                        columnMatch[column.key][index] = (columnMatch[column.key][index] || 0) + 1
                }

                // If this is a column to extract doctor information
                else if (column.doctor) {
                    // Remove `Dr.` and split by space for a doctor name array
                    //console.log(matchDoctorName, content, matchDoctorName.every((name) => content.toLowerCase().includes(name)))
                    // Checks for doctor regex match or matchDoctorName matches Strings inside content (i.e. matching doctor's name)
                    if(column.regex.some((regex) => content.match(regex)) || doctorNameList.some((doctorName) => isNameMatch(doctorName, content))) {
                        columnMatch[column.key][index] = (columnMatch[column.key][index] || 0) + 1
                        break
                    }
                }
                // If column is email
                if(column.email && column.regex.some((regex) => content.toLowerCase().match(regex))) {
                    columnMatch[column.key][index] = (columnMatch[column.key][index] || 0) + 1
                    break
                }

                // If there is a regex for this column that matches this content, increment the count
                else if (column.regex.some((regex) => content.match(regex))) {
                    columnMatch[column.key][index] = (columnMatch[column.key][index] || 0) + 1
                    // If matching this column is definitive
                    if (column.exclusive !== false)
                        break
                }
            }
        })
    })


    Object.keys(columnMatch).forEach((key) => {
        let columnIndex = Object.keys(columnMatch[key])

        // Delete all outliers
        if (key !== 'event' && columnIndex.length > 1) {
            columnIndex = columnIndex.sort((a, b) => (columnMatch[key][b] - columnMatch[key][a]))
            columnIndex.forEach((c) => {
                if (columnMatch[key][c] < columnMatch[key][columnIndex[0]])
                    delete columnMatch[key][c]
            })
        }
        // Remove outliers that have copies in events
        if (key !== 'event' && columnIndex.length > 1) {
            columnIndex.forEach((c) => {
                // Check if it has a same or bigger match in event keywords & no. of
                // keys matches is more than one
                if (columnMatch[key][c] <= columnMatch.event[c] && Object.keys(columnMatch[key]).length > 1)
                    delete columnMatch[key][c]
            })
        }

    })


    return columnMatch
}

/**
 * Extracts emails in a comma separated string from row data
 *
 * @param {[*]} rows
 * @param {Object} columnMatch
 * @returns {String} Comma separated email list
 */
function extractEmails(rows, columnMatch) {
    // Getting the email column from columnMatch
    const emailColumn = Object.keys(columnMatch.email)

    const emailArr = rows
        // Mapping emails from row data
        .map((row) => {
            // Getting email from email column
            const email  = (row[emailColumn] ? row[emailColumn] : '').toLowerCase().trim()
            // Checking if email matching email regex and is not part of ignore email list
            if(email.match(EMAIL_REGEX) && !IGNORE_EMAIL.includes(email) && !IGNORE_EMAIL_REGEX.test(email))
                return email
            else
                return false
        })
        // Filtering out emails from rows that weren't valid
        .filter((email) => email)
    return [...new Set(emailArr)].join(',')
}

function extractRows(rows, eventProviderKeyword, columnMatch, existingEmail) {
    return rows.filter(row => row.length > 0).map((row) => {
        var eventKeyword = {}, eventKeywords = []
        let data = { event: {}, row },
            duplicate = false

        const emailColumn = Object.keys(columnMatch.email)

        const email = (row[emailColumn] ? row[emailColumn] : '').toLowerCase().trim()
        const doctorColumn = Object.keys(columnMatch.doctor)[0]
        let rowProvider = matchProvider(row[doctorColumn], eventProviderKeyword)

        if(rowProvider) {
            data.provider = rowProvider.providerId
            data.doctorName = rowProvider.providerName
            eventKeyword = rowProvider.eventKeyword
            eventKeywords = Object.keys(eventKeyword)
        }

        // Iterate over each column in the row
        row.forEach((column, index) => {
            if(!isNaN(column))
                column = column.toString()
            column = column.trim()

            // Set the name of the extracted row
            if (columnMatch.name[index]) {
                // Remove any suffixes
                if (column.endsWith(', MD'))
                    column = column.substring(0, column.lastIndexOf(','))

                // Flip last, first to first last
                if (column.match(LAST_FIRST_REGEX)) {
                    let commaIndex = column.indexOf(',')
                    data.name = column.substring(commaIndex + 1).trim() + ' ' + column.substring(0, commaIndex)
                }
                else if (column.match(FIRST_LAST_REGEX)) {
                    data.name = column
                }
                else {
                    data.name = column
                }
            }

            // If this is an email address and is not a sentinel value that should be ignored (like no@email.com)
            else if (columnMatch.email[index] && isValidEmail(column)) {
                data.email = column.toLowerCase().trim()
            }

            else if (columnMatch.date && columnMatch.date[index]) {
                data.time = new Date(column).setHours(13, 0)
            }

            // If this is a phone number (skip for now)
            else if (columnMatch.phone && columnMatch.phone[index]) {
                if (column.match(LABELED_PHONE_REGEX)) {
                    let cellIndex = column.toLowerCase().indexOf('cell:')
                    if (cellIndex >= 0) {
                        column = column.substring(cellIndex + 5).trim()
                        // Remove the remainder of the number
                        if (column.indexOf(' ') > 0)
                            column = column.substring(0, column.indexOf(' '))
                        // If this is a valid phone number
                        if (column.match(PHONE_REGEX)) {
                            data.phone = column
                        }
                    }
                }
                // If the phone number is not set
                else if (data.phone == null && column.match(PHONE_REGEX)) {
                    data.phone = column
                }
            }

            // If this is a source of event keywords
            else if (columnMatch.time && columnMatch.time[index]) {
                data.hours = column
            }

            else if (columnMatch.event[index]) {
                let search = column.toLowerCase().trim()
                if (eventKeywords.includes(search))
                    Object.keys(eventKeyword[search]).forEach((eventId) => {
                        data.event[eventId] = (data.event[eventId] || 0) + 10
                    })
                else
                    eventKeywords.forEach((keyword) => {
                        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                        let matchRegex = new RegExp("\\b" + escapedKeyword + "\\b")
                        if (search.match(matchRegex))
                            Object.keys(eventKeyword[keyword]).forEach((eventId) => {
                                data.event[eventId] = (data.event[eventId] || 0) + 2
                            })
                    })

            }
        })

        return data
    })
    //Filtering out rows with dates of the past
    .filter(rowData => rowData.time >= (new Date()).setHours(0,0,0,0))
}


export default async function(rows, providerList, events, columnMatch) {
    const profileList = providerList.flatMap((provider) => provider.profile)
    const providerNameList = providerList.map((provider) => provider.name.toLowerCase().replace('dr.', '').trim().split(' '))
    var patients = [],
        eventProfile = eventProfileMap(events, profileList)

    const eventProviderKeyword = events.map((providerEvents) => ({
        ...providerEvents,
        eventKeyword: eventKeywordMap(providerEvents.events)
    }))


    let eventKeyword = eventProviderKeyword.flatMap((eventProvider) => Object.keys(eventProvider.eventKeyword))


    // Stores mapping of column key -> { column index -> count }
    if (! columnMatch)
        columnMatch = inferColumns(rows, eventKeyword, providerNameList)

    const existingPatients = await VuexStore.dispatch('provider/loadPatientsByEmail', { email: extractEmails(rows, columnMatch) })

    const scheduledEventMap = getScheduleEventMap()

    var existingEmail = patientEmailMap(existingPatients)


    var rowData = extractRows(rows, eventProviderKeyword, columnMatch, existingEmail)

    // Mapping for finding duplicates in the spreadsheet
    var patientEmail = {}, patientPhone = {}

    rowData.forEach((data) => {
        let patient = { row: [] }
        // Only parse rows that have an email address for the patient
        if (data.name && data.email) {
            // If the patient has already been loaded from another row
            if (patientEmail[data.email]) {
                patient = patientEmail[data.email]

                // Add invalid provider flag in case of provider not found in any row
                if(!data.provider)
                    patient.invalidProvider = true
                patient.row.push(data.row)
            }
            // If this is an existing patient or a new patient
            else {
                if (existingEmail[data.email]) {
                    let existingPatient = existingEmail[data.email]
                    patient.data = {
						id: existingPatient.id,
						name: existingPatient.given_name + ' ' + existingPatient.family_name,
						email: existingPatient.email,
						verified: existingPatient.verified,
						phone: existingPatient.phone || data.phone,
						schedule_event: existingPatient.schedule_event,
						provider_schedule_event: existingPatient.provider_schedule_event,
                        created: existingPatient.created,
                        existingProvider: existingPatient.provider,
                        existingProfile: existingPatient.profile
					}
                    patientEmail[data.email] = patient
                    patient.existing = true

					// Calculate future events
                    var now = Date.now()



                }
                // If this is a new patient
                else {
                    patient.data = {
                        name: data.name || '',
                        email: data.email || '',
                        phone: data.phone || '',
                    }
                    patientEmail[data.email] = patient
                    patient.enroll = true

                }
                // Mark the patient with invalid provider flag if provider not found in data
                if(!data.provider)
                    patient.invalidProvider = true

                patient.row.push(data.row)
                patients.push(patient)
            }

            /**
             * Current profile for the current matched provider
             * @type {Object}
             */
            const currentProvider = providerList.find((provider) => provider.provider === data.provider)

            /**
             * Matched profile id
             * @type {String}
             */
            let profile = null
            // If current provider exists and has a single `profile` exists in the provider

            try {
                if(currentProvider && Array.isArray(currentProvider.profile) && currentProvider.profile.length === 1)
                    profile = [currentProvider.profile[0].id]
            } catch (error) {
                console.error('Problem setting current profile', error)
            }


            if(profile)
                patient.hasProfile = true
            // if (data.hours && data.time) {
            //     const fullDate = new Date(data.time).toDateString()
            //     const newTime = new Date(fullDate + ',' + data.hours).getTime()
            //     data.time = isNaN(newTime) ? data.time : newTime
            // }
            // if(process.env.NODE_ENV === 'dev' )
            //     data.time = new Date(data.time).setHours(20, 0)

            // If there is an event associated with this row
            if (data.event) {
                // Getting all future events for the patient
                const futureEvents = getFutureEvents(patient.data.schedule_event, scheduledEventMap, data.provider)

                if (futureEvents.length > 0) {
					futureEvents.forEach((futureEvent) => {
                        //console.log('Event ', patient.data.email , new Date(parseInt(futureEvent.time)), new Date(data.time))
						// If the future event is on this time, then add to the weighting of its event
						if (sameDay(futureEvent.time, data.time)) {
                            data.event[futureEvent.event] = (data.event[futureEvent.event] || 0) + 100
						}
					})
				}

                // There are events that were matched
                //console.log(data.event)
                if (Object.keys(data.event).length > 0) {


                        // Object.keys(data.event).forEach((eventId) => {
                        //     // If this event was matched
                        //     if (data.event[eventId]) {
                        //         //
                        //         let scheduledEventTime = patient.data.schedule_event[eventId]
                        //         let scheduledTime = Object.keys(scheduledEventTime).find((time) => Math.abs(time - data.time) < 86400000)
                        //         if (scheduledTime != null) {
                        //             data.event[eventId] += 100
                        //         }
                        //     }
                        // })

                    var highestMatch = []
                    // List of all scores for all events, sorted in descending order
                    var eventMatchScore = Object.keys(data.event).map((e) => data.event[e]).sort((a, b) => (b - a))
                    // First score is the highest
                    var highestScore = eventMatchScore[0]

                    if (! patient.data.event)
                        patient.data.event = []

                    // List of all event IDs that were automatically matched
                    var matchEventIds = Object.keys(data.event)
                    // Highest matching keys (equal to the highest match score)
                    var match = matchEventIds.filter((e) => (data.event[e] == highestScore))
                    //console.log(match)
                    var matchId = match.length == 1 ? match[0] : null

                    var existingTimes = futureEvents ? futureEvents.filter((eventObj) => eventObj.event === matchId) : []
                    var existingEvent = null
                    var rescheduleEvent = false

                    if (matchId && existingTimes.length > 0) {
                        existingEvent = existingTimes.find((eventObj) => sameDay(eventObj.time, data.time))
                        rescheduleEvent = existingTimes[0]
                    }

                    if (existingEvent) {
                        patient.sameSchedule = true
                        if(!patient.scheduleData)
                            patient.scheduleData = []
                        patient.scheduleData.push({
                            id: matchId,
                            match,
                            time: data.time,
                            row: patient.row.length - 1,
                            futureEvents,
                            provider: data.provider
                        })
                    }
                    else if (rescheduleEvent) {
                        patient.reschedule = true
                        if(! patient.rescheduleData)
                            patient.rescheduleData = []

                        patient.rescheduleData.push({
                            id: rescheduleEvent.id,
                            match,
                            matchId,
                            previous_time: parseInt(rescheduleEvent.time),
                            time: data.time,
                            row: patient.row.length - 1,
                            provider: data.provider
                        })
                    }
                    else {
                        // Patient requires confirmation
                        if (match.length > 1)
                            patient.confirmed = false
                        patient.data.event.push({
                            id: matchId,
                            match,
                            time: data.time,
                            row: patient.row.length - 1,
                            provider: data.provider,
                            profile,
                            futureEvents
                        })
                    }
                }
                else {
                    if(! patient.data.event)
                        patient.data.event = []
                    patient.data.event.push({
                        id : null,
                        match: [],
                        time: data.time,
                        row: patient.row.length - 1,
                        provider: data.provider,
                        profile,
                        futureEvents
                    })
                    patient.confirmed = false
                }

            }
        }
        else {
            patients.push({
                data,
                row: [ data.row ],
                invalid: true
            })
        }
    })

    return {
        patient: patients,
        column: columnMatch
    }
}
